<template>
  <div class="iso-edit-page iso-scope-codes-edit">
    <!-- ===================== Hero ===================== -->
    <div class="iso-edit-hero">
      <div class="iso-edit-hero__blob iso-edit-hero__blob--one" />
      <div class="iso-edit-hero__blob iso-edit-hero__blob--two" />

      <div class="iso-edit-hero__content">
        <div class="iso-edit-hero__icon">
          <feather-icon
            icon="HashIcon"
            size="26"
          />
        </div>
        <div>
          <h2 class="iso-edit-hero__title">
            ISO Scope Codes
          </h2>
          <p class="iso-edit-hero__subtitle">
            Manage IAF scope codes for certificate group
            <strong>#{{ certificateId }}</strong>
          </p>
        </div>
      </div>

      <div class="iso-scope-codes-edit__hero-actions">
        <button
          type="button"
          class="iso-edit-back"
          @click="goBack"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="16"
            class="mr-50"
          />
          Back
        </button>
        <button
          type="button"
          class="iso-scope-add-btn"
          @click="openAddModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="mr-50"
          />
          Add Code
        </button>
      </div>
    </div>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="iso-scope-toolbar mb-1"
    >
      <div class="iso-scope-toolbar__inner">
        <div class="iso-scope-toolbar__meta">
          <span class="iso-scope-toolbar__count">
            {{ filteredCodes.length }}
            <small>{{ filteredCodes.length === 1 ? 'code' : 'codes' }}</small>
          </span>
          <span
            v-if="searchQuery && filteredCodes.length !== codes.length"
            class="iso-scope-toolbar__hint"
          >
            of {{ codes.length }} total
          </span>
        </div>

        <b-input-group class="iso-scope-search">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by code or name..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="searchQuery"
            is-text
            class="iso-scope-search__clear"
            @click="searchQuery = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="pageLoading">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-tpl-skel">
          <div class="iso-tpl-skel__line iso-tpl-skel__line--title" />
          <div class="iso-tpl-skel__row">
            <div class="iso-tpl-skel__icon" />
            <div class="iso-tpl-skel__btn" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty ===================== -->
    <b-card
      v-else-if="!filteredCodes.length"
      class="iso-edit-shell text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>{{ searchQuery ? 'No matching codes' : 'No scope codes yet' }}</h4>
      <p class="text-muted mb-2">
        {{ searchQuery ? 'Try adjusting your search.' : 'Add the first IAF scope code for this certificate group.' }}
      </p>
      <button
        v-if="!searchQuery"
        type="button"
        class="iso-tpl-btn iso-tpl-btn--save"
        @click="openAddModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="15"
          class="mr-50"
        />
        Add Code
      </button>
    </b-card>

    <!-- ===================== Codes grid ===================== -->
    <b-row
      v-else
      class="iso-tpl-grid"
    >
      <b-col
        v-for="item in filteredCodes"
        :key="item.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-code-card">
          <div class="iso-code-card__accent" />

          <div class="iso-code-card__body">
            <div class="iso-code-card__top">
              <div class="iso-code-card__icon">
                <feather-icon
                  icon="BookmarkIcon"
                  size="18"
                />
              </div>
              <span
                class="iso-code-card__badge"
                :title="item.code"
              >
                {{ item.code }}
              </span>
            </div>

            <h4
              class="iso-code-card__name"
              :title="item.name"
            >
              {{ item.name }}
            </h4>

            <div class="iso-code-card__actions">
              <button
                type="button"
                class="iso-tpl-btn iso-tpl-btn--edit"
                @click="openEditModal(item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                  class="mr-50"
                />
                Edit
              </button>
              <button
                type="button"
                class="iso-tpl-btn iso-tpl-btn--delete"
                :disabled="deletingId === item.id"
                @click="confirmDelete(item)"
              >
                <b-spinner
                  v-if="deletingId === item.id"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="Trash2Icon"
                  size="14"
                  class="mr-50"
                />
                {{ deletingId === item.id ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Add / Edit modal ===================== -->
    <b-modal
      id="iso-scope-code-modal"
      v-model="formModal"
      centered
      hide-footer
      size="md"
      modal-class="iso-scope-modal"
      :title="isEditMode ? 'Edit Scope Code' : 'Add Scope Code'"
      @hidden="resetForm"
    >
      <div class="iso-scope-modal__body">
        <div class="iso-tpl-edit-label">
          <feather-icon
            icon="HashIcon"
            size="14"
            class="mr-50"
          />
          Code
        </div>
        <b-form-input
          v-model="form.code"
          class="mb-1"
          placeholder="e.g. IAF CODE 3"
          :state="codeState"
        />
        <small
          v-if="codeState === false"
          class="text-danger d-block mb-1"
        >
          Code is required.
        </small>

        <div class="iso-tpl-edit-label">
          <feather-icon
            icon="TypeIcon"
            size="14"
            class="mr-50"
          />
          Name
        </div>
        <b-form-textarea
          v-model="form.name"
          rows="3"
          max-rows="6"
          placeholder="e.g. Food products, beverages and tobacco"
          :state="nameState"
        />
        <small
          v-if="nameState === false"
          class="text-danger d-block mt-50"
        >
          Name is required.
        </small>

        <div class="iso-scope-modal__actions">
          <button
            type="button"
            class="iso-tpl-btn iso-tpl-btn--cancel"
            :disabled="isSaving"
            @click="formModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="iso-tpl-btn iso-tpl-btn--save"
            :disabled="isSaving"
            @click="submitForm"
          >
            <b-spinner
              v-if="isSaving"
              small
              type="grow"
              class="mr-50"
            />
            <feather-icon
              v-else
              :icon="isEditMode ? 'CheckIcon' : 'PlusIcon'"
              size="15"
              class="mr-50"
            />
            {{ isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Code') }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BRow, BCol, BCard, BModal, BFormInput, BFormTextarea, BSpinner,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BModal,
    BFormInput,
    BFormTextarea,
    BSpinner,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
  },
  data() {
    return {
      pageLoading: false,
      codes: [],
      searchQuery: '',
      formModal: false,
      isEditMode: false,
      isSaving: false,
      deletingId: null,
      editingId: null,
      form: {
        code: '',
        name: '',
      },
      formTouched: false,
    }
  },
  computed: {
    certificateId() {
      return this.$route.params.id
    },
    filteredCodes() {
      const term = this.searchQuery.trim().toLowerCase()
      if (!term) return this.codes

      return this.codes.filter(item => {
        const code = (item.code || '').toLowerCase()
        const name = (item.name || '').toLowerCase()
        const id = String(item.id || '')
        return code.includes(term) || name.includes(term) || id.includes(term)
      })
    },
    codeState() {
      if (!this.formTouched) return null
      return this.form.code.trim().length > 0
    },
    nameState() {
      if (!this.formTouched) return null
      return this.form.name.trim().length > 0
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.fetchCodes()
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'iso-scope-codes' })
    },

    toast(title, variant = 'danger', icon = 'AlertTriangleIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    },

    async fetchCodes() {
      this.pageLoading = true

      try {
        const response = await axios.get(`iso/scopes/${this.$route.params.id}/codes`)

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data
          this.codes = Array.isArray(data) ? data : []
        }
      } catch (error) {
        this.toast(error.response?.data?.message || 'Failed to load scope codes')
      } finally {
        this.pageLoading = false
      }
    },

    openAddModal() {
      this.isEditMode = false
      this.editingId = null
      this.form = { code: '', name: '' }
      this.formTouched = false
      this.formModal = true
    },

    openEditModal(item) {
      this.isEditMode = true
      this.editingId = item.id
      this.form = {
        code: item.code || '',
        name: item.name || '',
      }
      this.formTouched = false
      this.formModal = true
    },

    resetForm() {
      this.isEditMode = false
      this.editingId = null
      this.form = { code: '', name: '' }
      this.formTouched = false
      this.isSaving = false
    },

    async submitForm() {
      this.formTouched = true
      if (!this.form.code.trim() || !this.form.name.trim()) return

      this.isSaving = true
      const payload = {
        _method: this.isEditMode ? 'PUT' : 'POST',
        code: this.form.code.trim(),
        name: this.form.name.trim(),
      }

      try {
        if (this.isEditMode) {
          await axios.post(`iso/scope-codes/${this.editingId}`, payload)
          this.toast('Scope code updated successfully', 'success', 'CheckIcon')
        } else {
          await axios.post(`iso/scopes/${this.$route.params.id}/codes`, payload)
          this.toast('Scope code added successfully', 'success', 'CheckIcon')
        }

        this.formModal = false
        await this.fetchCodes()
      } catch (error) {
        this.toast(
          decryptData(error.response.data.payload)?.message?.code[0] || decryptData(error.response.data.payload)?.message
            || (this.isEditMode ? 'Failed to update scope code' : 'Failed to add scope code'),
        )
      } finally {
        this.isSaving = false
      }
    },

    confirmDelete(item) {
      this.$swal({
        title: 'Delete this scope code?',
        html: `<strong>${item.code || ''}</strong><br/><span style="color:#6e6b7b">${item.name || ''}</span>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6e6b7b',
        confirmButtonText: 'Yes, delete it',
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteCode(item)
        }
      })
    },

    async deleteCode(item) {
      this.deletingId = item.id

      try {
        await axios.delete(`iso/scope-codes/${item.id}`)
        this.$swal('Deleted!', 'Scope code deleted successfully.', 'success')
        await this.fetchCodes()
      } catch (error) {
        this.toast(error.response?.data?.message || 'Failed to delete scope code')
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped src="../EditIsoTemplate.scss"></style>
<style lang="scss" scoped src="../EditScopes.scss"></style>

<!-- Modal is portaled to body — keep button/form styles available -->
<style lang="scss">
.iso-scope-modal {
  .iso-tpl-edit-label {
    display: flex;
    align-items: center;
    margin-bottom: 0.4rem;
    color: #5f736f;
    font-size: 0.82rem;
    font-weight: 700;
  }

  .iso-tpl-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 0.9rem;
    border: 0;
    border-radius: 0.55rem;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    &--save {
      color: #fff;
      background: linear-gradient(135deg, #0d9488, #059669);
      box-shadow: 0 6px 14px -6px rgba(13, 148, 136, 0.45);
    }

    &--cancel {
      color: #6e6b7b;
      background: #f3f3f5;
    }
  }

  .iso-scope-modal__actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1.15rem;
  }
}
</style>
